/**
 * Pure calculation functions extracted for testability
 * These functions have no side effects and can be unit tested in isolation
 */

/**
 * Calculate percentage growth between two values
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @returns {number} - Growth percentage (rounded)
 */
export function calculateGrowth(current, previous) {
  if (previous === 0) return current > 0 ? 100 : 0
  return Math.round(((current - previous) / previous) * 100)
}

/**
 * Calculate PHE (Predictable Human Error) rate from pre-aggregated counts.
 *
 * Use this when you already have wrongCount and totalAnswers as numbers
 * (e.g., from DB aggregate queries). For per-session PHE from raw cycle
 * data, use metricsService.calculatePHE() instead.
 *
 * @param {number} wrongCount - Number of wrong answers (excluding "I need to learn")
 * @param {number} totalAnswers - Total answers attempted (excluding "I need to learn")
 * @returns {number} - PHE percentage (0-100)
 */
export function calculatePHERate(wrongCount, totalAnswers) {
  if (totalAnswers === 0) return 0
  return Math.round((wrongCount / totalAnswers) * 100)
}

/**
 * Calculate median of an array of numbers
 * @param {number[]} values - Array of numbers
 * @returns {number} - Median value
 */
export function calculateMedian(values) {
  if (!values || values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2
}

/**
 * Distribute scores into standard ranges
 * @param {number[]} scores - Array of score percentages
 * @returns {Array<{range: string, learners: number}>}
 */
export function getScoreDistribution(scores) {
  const distribution = [
    { range: '0-40%', learners: 0 },
    { range: '41-60%', learners: 0 },
    { range: '61-80%', learners: 0 },
    { range: '81-100%', learners: 0 }
  ]

  scores.forEach(score => {
    if (score <= 40) distribution[0].learners++
    else if (score <= 60) distribution[1].learners++
    else if (score <= 80) distribution[2].learners++
    else distribution[3].learners++
  })

  return distribution
}

/**
 * Evaluate if an answer is correct
 * @param {number} answerIndex - Selected answer index
 * @param {number} correctAnswer - Correct answer index
 * @param {number} confidence - Confidence level (0 = needs learning)
 * @returns {boolean|null} - true/false for correct/wrong, null for needs learning
 */
export function evaluateAnswer(answerIndex, correctAnswer, confidence) {
  if (confidence === 0) return null  // "I need to learn" selected
  return answerIndex === correctAnswer
}

/**
 * Get the next attempt number for a session
 * @param {Array<{attempt_number: number}>} previousAttempts
 * @returns {number}
 */
export function getNextAttemptNumber(previousAttempts) {
  if (!previousAttempts || previousAttempts.length === 0) return 1
  return previousAttempts[0].attempt_number + 1
}
